import { useEffect } from 'react'

const LAST_UPDATED = 'June 3, 2026'

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    text: 'By accessing or using Co-Eater (the "Service"), you agree to be bound by these Terms of Use ("Terms"). If you do not agree to these Terms, please do not use the Service. These Terms apply to all visitors, registered users, and any other party who accesses or uses the Service.',
  },
  {
    id: 'eligibility',
    title: '2. Eligibility',
    bullets: [
      'You must be at least 13 years of age to use Co-Eater.',
      'By using Co-Eater, you represent that you have the legal capacity to enter into a binding agreement.',
      'If you are using the Service on behalf of an organisation, you represent that you are authorised to bind that organisation to these Terms.',
    ],
  },
  {
    id: 'account',
    title: '3. Your Account',
    content: [
      {
        sub: 'Registration',
        text: 'You may register using Google Sign-In. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.',
      },
      {
        sub: 'Accurate Information',
        text: 'You agree to provide accurate, current, and complete information during registration and to keep your profile information up to date.',
      },
      {
        sub: 'Account Security',
        text: 'You must notify us immediately if you become aware of any unauthorised use of or access to your account. We are not liable for any loss or damage arising from your failure to protect your account.',
      },
      {
        sub: 'One Account Per Person',
        text: 'You may not create more than one account per person. Duplicate or fraudulent accounts may be removed without notice.',
      },
    ],
  },
  {
    id: 'acceptable-use',
    title: '4. Acceptable Use',
    text: 'You agree to use Co-Eater only for lawful purposes and in a manner that does not infringe the rights of others. You must not:',
    bullets: [
      'Post false, misleading, or deceptive content.',
      'Harass, threaten, bully, or intimidate other users.',
      'Share content that is unlawful, obscene, defamatory, discriminatory, or otherwise objectionable.',
      'Use the Service to send unsolicited commercial messages (spam).',
      'Impersonate any person or entity, or misrepresent your affiliation with any person or entity.',
      'Attempt to access another user\'s account or any non-public portion of the Service.',
      'Use automated tools (bots, scrapers) to access, collect, or interact with the Service.',
      'Interfere with the security, integrity, or performance of the Service.',
      'Violate any applicable local, national, or international law or regulation.',
    ],
  },
  {
    id: 'user-content',
    title: '5. User-Generated Content',
    content: [
      {
        sub: 'Ownership',
        text: 'You retain ownership of content you submit or post on Co-Eater, including profile information, messages, photos, and ratings ("User Content").',
      },
      {
        sub: 'Licence to Co-Eater',
        text: 'By submitting User Content, you grant Co-Eater a non-exclusive, worldwide, royalty-free, sublicensable licence to use, reproduce, modify, display, and distribute your User Content solely for the purpose of operating and improving the Service.',
      },
      {
        sub: 'Your Responsibility',
        text: 'You are solely responsible for the User Content you submit. You represent that you own or have the necessary rights to share such content, and that it does not violate any third-party rights.',
      },
      {
        sub: 'Content Removal',
        text: 'We reserve the right to remove any User Content that violates these Terms or that we deem harmful, offensive, or otherwise inappropriate, without prior notice.',
      },
    ],
  },
  {
    id: 'safety',
    title: '6. Safety & In-Person Meetings',
    text: 'Co-Eater facilitates connections between people, but we are not responsible for the conduct of users in person. When meeting someone for a meal, please exercise common sense and take reasonable safety precautions — meet in public places, inform someone you trust of your plans, and trust your instincts. Co-Eater does not conduct background checks on its users.',
  },
  {
    id: 'blocking-reporting',
    title: '7. Blocking & Reporting',
    text: 'If another user behaves in a way that makes you uncomfortable or violates these Terms, you may block them or report them using the in-app tools. We take all reports seriously and will investigate promptly. Depending on our findings, we may warn, suspend, or permanently ban the reported user.',
  },
  {
    id: 'intellectual-property',
    title: '8. Intellectual Property',
    text: 'All content, design, code, logos, and other materials provided by Co-Eater (excluding User Content) are the property of Co-Eater or its licensors and are protected by intellectual property laws. You may not copy, modify, distribute, or create derivative works from any Co-Eater content without our prior written permission.',
  },
  {
    id: 'third-party',
    title: '9. Third-Party Services',
    text: 'The Service may contain links to or integrations with third-party websites and services (such as Google Maps, restaurant listings, or payment processors). These third parties have their own privacy policies and terms of service. Co-Eater is not responsible for the practices or content of any third-party service.',
  },
  {
    id: 'disclaimers',
    title: '10. Disclaimers',
    bullets: [
      'The Service is provided "as is" and "as available" without warranties of any kind, express or implied.',
      'We do not guarantee that the Service will be uninterrupted, secure, or error-free.',
      'We do not warrant the accuracy, completeness, or reliability of any content on the Service.',
      'Co-Eater is not responsible for verifying the identity of users or the accuracy of profile information.',
    ],
  },
  {
    id: 'liability',
    title: '11. Limitation of Liability',
    text: 'To the maximum extent permitted by applicable law, Co-Eater and its directors, employees, partners, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service — including damages for personal injury, lost profits, lost data, or loss of goodwill — even if we have been advised of the possibility of such damages. Our total liability to you for any claims arising out of or relating to these Terms or the Service shall not exceed INR 1,000.',
  },
  {
    id: 'indemnification',
    title: '12. Indemnification',
    text: 'You agree to indemnify and hold harmless Co-Eater, its affiliates, and their respective directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or relating to: (a) your use of the Service; (b) your User Content; (c) your violation of these Terms; or (d) your violation of any third-party rights.',
  },
  {
    id: 'termination',
    title: '13. Termination',
    text: 'We reserve the right to suspend or terminate your account and access to the Service at any time, with or without cause, and with or without notice. You may also delete your account at any time from the Settings page. Upon termination, these Terms (including any provisions that by their nature should survive) remain in effect.',
  },
  {
    id: 'governing-law',
    title: '14. Governing Law',
    text: 'These Terms are governed by and construed in accordance with the laws of India. Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts located in Bengaluru, Karnataka, India.',
  },
  {
    id: 'changes',
    title: '15. Changes to These Terms',
    text: 'We may update these Terms from time to time. We will notify you of material changes by displaying a notice within the app or by email. The "Last Updated" date at the top of this page reflects the most recent revision. Your continued use of the Service after changes take effect constitutes your acceptance of the updated Terms.',
  },
  {
    id: 'contact',
    title: '16. Contact Us',
    text: 'If you have any questions about these Terms of Use, please contact us at: legal@coeater.com',
  },
]

export default function TermsOfUse() {
  useEffect(() => {
    document.title = 'Terms of Use — Co-Eater'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Read the Co-Eater Terms of Use to understand the rules for using our social dining platform, your rights, and our responsibilities.',
      )
    }
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 px-4 py-6 sm:px-8">
    <article className="max-w-3xl mx-auto w-full">
      {/* Header */}
      <header className="mb-8">
        <h1 className="title-font text-3xl font-extrabold text-gray-900 mb-2">Terms of Use</h1>
        <p className="text-sm text-gray-500">Last Updated: {LAST_UPDATED}</p>
        <p className="mt-4 text-gray-600 leading-relaxed">
          Please read these Terms of Use carefully before using Co-Eater. They govern your access to and
          use of our social dining platform, website, and related services.
        </p>
      </header>

      {/* Table of Contents */}
      <nav className="bg-orange-50 rounded-2xl p-5 mb-8 border border-orange-100" aria-label="Table of contents">
        <h2 className="font-bold text-gray-800 mb-3 text-sm">Contents</h2>
        <ol className="grid sm:grid-cols-2 gap-1.5 text-sm">
          {SECTIONS.map(s => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-orange-600 hover:underline">
                {s.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {/* Sections */}
      <div className="space-y-8">
        {SECTIONS.map(s => (
          <section key={s.id} id={s.id} className="scroll-mt-4">
            <h2 className="title-font text-lg font-bold text-gray-900 mb-3">{s.title}</h2>
            {s.text && <p className="text-sm text-gray-600 leading-relaxed mb-3">{s.text}</p>}
            {s.bullets && (
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600">
                {s.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            )}
            {s.content && (
              <dl className="space-y-4">
                {s.content.map(item => (
                  <div key={item.sub}>
                    <dt className="font-semibold text-gray-800 text-sm mb-1">{item.sub}</dt>
                    <dd className="text-sm text-gray-600 leading-relaxed">{item.text}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>
        ))}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-10 pt-6 pb-10 text-center">
        <p className="title-font font-bold text-gray-800 mb-3">Co-Eater</p>
        <nav className="flex flex-wrap gap-4 justify-center text-xs" aria-label="Footer navigation">
          <a href="/about" className="text-gray-600 hover:text-orange-600 font-medium transition">About</a>
          <span className="text-gray-300">|</span>
          <a href="/privacy-policy" className="text-gray-600 hover:text-orange-600 font-medium transition">Privacy Policy</a>
          <span className="text-gray-300">|</span>
          <a href="/terms-of-use" className="text-orange-600 font-semibold">Terms of Use</a>
        </nav>
        <p className="text-xs text-gray-400 mt-4">&copy; {new Date().getFullYear()} Co-Eater. All rights reserved.</p>
      </footer>
    </article>
    </div>
  )
}
